import React from 'react';

type Step = {
  id: string;
  name: string;
};

interface StepIndicatorProps {
  currentStep: string;
  steps: Step[];
  className?: string;
  compact?: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, steps, className = '', compact = false }) => {
  const currentStepIndex = Math.max(steps.findIndex(step => step.id === currentStep), 0);
  const progressPercent = steps.length > 1 ? (currentStepIndex / (steps.length - 1)) * 100 : 0;
  
  // Responsive sizing
  const dotSize = compact ? 'w-7 h-7' : 'w-8 h-8';
  const labelClass = compact ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm';

  return (
    <nav aria-label="Progress" className={className}>
      <div className="px-6 sm:px-8 py-4">
        {/* Labels Row */}
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          {steps.map((step, stepIdx) => {
            const isCompleted = currentStepIndex > stepIdx;
            const isActive = currentStepIndex === stepIdx;
            const labelColor = isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground';

            return (
              <div 
                key={step.id}
                className="flex-1 flex justify-center text-center px-2 min-h-[2.5rem] sm:min-h-[3rem]"
              >
                <span className={`${labelClass} font-semibold leading-tight ${labelColor} max-w-[90px]`}>
                  {step.name}
                </span>
              </div>
            );
          })}
        </div>

        {/* Dots Row with Line */}
        <div className="relative flex items-center justify-between">
          {/* Background gray line - spans from first to last dot center */}
          <div 
            className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-border"
            style={{
              left: `calc((100% / ${steps.length}) / 2)`,
              right: `calc((100% / ${steps.length}) / 2)`,
            }}
            aria-hidden="true"
          />
          
          {/* Primary progress line */}
          <div 
            className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-primary transition-all duration-300"
            style={{
              left: `calc((100% / ${steps.length}) / 2)`,
              width: `calc(((100% - (100% / ${steps.length})) * ${progressPercent / 100}))`,
            }}
            aria-hidden="true"
          />

          {/* Dots */}
          {steps.map((step, stepIdx) => {
            const isCompleted = currentStepIndex > stepIdx;
            const isActive = currentStepIndex === stepIdx;
            const innerDot = isCompleted || isActive;

            return (
              <div 
                key={step.id}
                className="flex-1 flex justify-center"
              >
                <div
                  className={`relative z-10 rounded-full border-2 transition-all duration-300 flex items-center justify-center bg-background shadow-sm ${dotSize} ${
                    innerDot ? 'border-primary' : 'border-border'
                  }`}
                  title={`${step.name}${isCompleted ? ' - Completed' : isActive ? ' - Current' : ' - Upcoming'}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span
                    className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                      innerDot ? 'bg-primary' : 'bg-transparent'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                
                {/* Screen reader text */}
                <span className="sr-only">
                  {step.name} {isCompleted ? '- Completed' : isActive ? '- Current' : '- Upcoming'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default StepIndicator;
